/*
 * @Description: 动作面板组件，基于Popup + Picker
 */
'use client';

import { useTranslations } from 'next-intl';
import React, { ReactNode, useEffect, useState } from 'react';
import Picker from '../Picker';
import Popup from '../Popup';

export interface ActionSheetOption {
  /**
   * 选项标签
   */
  label: string;
  /**
   * 选项值
   */
  value: any;
  /**
   * 是否禁用
   */
  disabled?: boolean;
}

export interface ActionSheetProps {
  /**
   * 是否可见
   */
  open?: boolean;
  /**
   * 面板标题
   */
  title: string;
  /**
   * 标题类名
   */
  titleClassName?: string;
  /**
   * 选项列表
   */
  options: ActionSheetOption[];
  /**
   * 默认选中的值
   */
  defaultValue?: any;
  /**
   * 当前选中的值
   */
  value?: any;
  /**
   * 选项高度
   */
  optionHeight?: number | string;
  /**
   * 可见选项数量
   */
  visibleOptionNum?: number;
  /**
   * 取消按钮文本
   */
  cancelText?: string;
  /**
   * 确认按钮文本
   */
  saveText?: string;
  /**
   * 自定义选项渲染函数
   */
  renderOption?: (option: ActionSheetOption, index: number) => ReactNode;
  /**
   * 选中值变化时的回调
   */
  onChange?: (value: any, option: ActionSheetOption) => void;
  /**
   * 确认按钮点击回调
   */
  onSave?: (value: any, option: ActionSheetOption) => void;
  /**
   * 取消按钮点击回调
   */
  onCancel?: () => void;
  /**
   * 关闭回调
   */
  onClose?: () => void;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 自定义 z-index 值
   */
  zIndex?: number;
}

const ActionSheet: React.FC<ActionSheetProps> = (props) => {
  const t = useTranslations('actionSheet');
  const {
    open = false,
    title,
    titleClassName = '',
    options = [],
    defaultValue,
    value,
    optionHeight = 44,
    visibleOptionNum = 7,
    cancelText = t('cancelText'),
    saveText = t('saveText'),
    renderOption,
    onChange,
    onSave,
    onCancel,
    onClose,
    className = '',
    zIndex,
  } = props;
  // 当前选中的选项
  const [selectedValue, setSelectedValue] = useState<any>(value || defaultValue);
  // ios 采用useEffect初始化选中项 会有bug
  const [selectedOption, setSelectedOption] = useState<ActionSheetOption | null>(() => {
    if (defaultValue) {
      return options.find((opt) => opt.value === defaultValue) || null;
    }
    return null;
  });
  const [isDirty, setIsDirty] = useState(false);

  // 当外部value变化时，更新内部状态
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
      const option = options.find((opt) => opt.value === value) || null;
      setSelectedOption(option);
    } else {
      const option = options[0];
      setSelectedOption(option);
    }
  }, [value, options]);

  // useEffect(() => {
  //   if (defaultValue) {
  //     const option = options.find((opt) => opt.value === defaultValue) || null;
  //     setSelectedOption(option);
  //   }
  // }, []);

  // 初始化选中项
  useEffect(() => {
    if (selectedOption === null && options.length > 0) {
      if (!isDirty && defaultValue) {
        const option = options.find((opt) => opt.value === defaultValue) || null;
        setSelectedOption(option);
        return;
      }

      const initialValue = value !== undefined ? value : defaultValue;
      const option =
        initialValue !== undefined
          ? options.find((opt) => opt.value === initialValue) || options[0]
          : options[0];

      if (option) {
        setSelectedOption(option);
        setSelectedValue(option.value);
      }
    }
  }, [options, defaultValue, value, selectedOption]);

  const handleChange = (newValue: any, option: ActionSheetOption) => {
    setSelectedValue(newValue);
    setSelectedOption(option);
    onChange && onChange(newValue, option);
  };

  const handleSave = () => {
    setIsDirty(true);
    if (selectedOption) {
      onSave && onSave(selectedValue, selectedOption);
    }
    onClose && onClose();
  };

  const handleCancel = () => {
    onCancel && onCancel();
    onClose && onClose();
  };

  // 自定义标题组件 - 固定样式 (Cancel + Title + Save)
  const customTitle = (
    <div className="h-[11.733vw] bg-white flex items-center justify-between">
      <button onClick={handleCancel} className="text-[#969799] px-[4.267vw] text-[3.733vw]">
        {cancelText}
      </button>
      <div
        className={`font-bb text-[4.533vw] text-primary-text leading-[4.533vw] text-center ${titleClassName}`}
      >
        {title}
      </div>
      <button
        onClick={handleSave}
        className=" px-[4.267vw] text-[3.733vw] font-br text-primary-purple"
      >
        {saveText}
      </button>
    </div>
  );

  useEffect(() => {
    if (open) {
      const currentValue = value;

      // TODO:在弹窗打开的渲染队列中还没有渲染完成，所以需要延迟执行，有什么方法优化吗？
      setTimeout(() => {
        const items = document.querySelectorAll('.current-value-picker');
        items.forEach((item) => {
          if (item.getAttribute('data-value') === currentValue) {
            item.scrollIntoView({ block: 'center' });
          }
        });
      }, 0);
    }
  }, [open]);

  return (
    <Popup
      position="bottom"
      height="400px"
      open={open}
      onClose={handleCancel}
      title={customTitle}
      zIndex={zIndex}
      sx={{
        '& .popup-content': {
          padding: '16px 0px',
        },
      }}
    >
      <div className={`action-sheet ${className}`}>
        {/* defaultValue为什么取的是lable？ */}
        <Picker
          options={options}
          defaultValue={selectedOption?.label}
          optionHeight={optionHeight}
          visibleOptionNum={visibleOptionNum}
          onChange={(_, option) => handleChange(option.value, option)}
          renderOption={renderOption}
        />
      </div>
    </Popup>
  );
};

export default ActionSheet;
